import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import SubpageHero from "@/components/SubpageHero";
import {
  Heart, Lightbulb, Target, Microscope, GraduationCap, Award,
  Brain, Beaker, Dna, Activity, HeartPulse, Droplets, Shield,
  ArrowRight, Sparkles, Utensils, LineChart, Zap, Layers,
} from "lucide-react";

const TX = {
  bg: {
    title: "За Nutramilo",
    desc: "Мрежа от 20 физиологични близнака, свързани с над 110 зависимости, които виждат тялото Ви като една система, а не като отделни изследвания. Обмяна, лаборатория, сън, сърце, напрежение, хормони, устна кухина и гръдно здраве — на едно място, с посочен източник за всяка оценка.",
    back: "Начало",
    mission_title: "Нашата мисия",
    mission_p1: "Да направим метаболитно ориентираното хранене достъпно, измеримо и наистина персонално.",
    mission_p2: "Реакцията към една и съща чиния може да бъде различна при двама души — заради инсулинова чувствителност, β-клетъчна функция, телесен състав и хормонален статус. Универсалните диети пропускат тази индивидуалност.",
    mission_p3: "Nutramilo ползва тези принципи, за да предложи метаболитен модел, изграден върху вашите реални данни.",
    twin_title: "Мрежа от физиологични близнаци",
    twin_subtitle: "20 близнака · една жива система",
    twin_p1: "Nutramilo не е поредният уред за отчитане на стъпки. Това е мрежа от 20 близнака за отделните системи на организма — хранене, лаборатория, наследственост, микробиом, телесен състав, сън, напрежение, сърце, хормони, обмяна и инсулин, възпаление, движение, черен дроб, бъбреци, зъби, гърда, кръв, нервна система, мъжко здраве и дишане — обединени в една обща оценка.",
    twin_p2: "Когато отбележите хранене, измерите кръвно налягане или качите изследване, всички засегнати близнаци се обновяват наведнъж. Над 110 физиологични зависимости показват какво какво влече: как напрежението тази седмица ще срещне инсулиновата Ви крива, сърцето и зъбите следващата. Всяка оценка се отваря и показва от какво е съставена.",
    twin_features: [
      "Разговор с близнаците, включително с гласов въпрос — отговорът се съставя само от Вашите данни",
      "Карта на показателите — 31 показателя в 10 групи, всеки с точно един отговорен близнак",
      "Симулатор на чинията — инсулинов товар, кортизолов отговор, кръвни мазнини и бъбречен товар",
      "Плочка „Днес“ — едно кратко въвеждане задвижва няколко близнака наведнъж",
      "Над 110 физиологични връзки и откриване на порочни кръгове",
      "Биологични пътища в клетката, всеки със съответния близнак",
    ],
    consultants_cta_title: "Зад всеки близнак стои лекар",
    consultants_cta_sub: "Нашите медицински консултанти проверяват алгоритмите и протоколите. Запознайте се с екипа.",
    consultants_cta_btn: "Вижте консултантите",
    dil_title: "Инсулинов товар",
    dil_p1: "Сърцето на платформата е инсулиновият товар — измерим модел, който оценява инсулиновия отговор на храненето, а не само калориите.",
    dil_p2: "За разлика от броенето на калории или гликемичния индекс, инсулиновият товар отчита съвместния ефект на въглехидрати и протеини и отразява реалния хормонален отговор.",
    dil_p3: "Използваме инсулиновия товар като показател за обмяната, а не като самостоятелен предсказател на заболяване.",
    vision_title: "Визия и идеология",
    vision_p1: "Не всички калории са метаболитно равностойни. Инсулиновият отговор зависи от макронутриентен баланс, фибри, тип мазнини, степен на преработка и индивидуален контекст.",
    vision_p2: "Не просто броим калории — анализираме метаболитното натоварване на всяко хранене в контекста на конкретния човек.",
    personalization_title: "Персонализация",
    personalization_p1: "Разграничаваме пет метаболитни профила според контрола на кръвната захар, поведението на инсулина, кръвните мазнини и общата метаболитна натовареност.",
    personalization_p2: "Препоръките се пренастройват в реално време спрямо текущото Ви състояние и начин на хранене.",
    ai_title: "Интелигентна система",
    ai_p1: "Платформата съчетава четири специализирани модела, всеки със собствена научна база:",
    ai_agents: [
      { name: "Хормони и обмяна", desc: "Инсулин, щитовидна жлеза, кортизол, полови хормони", icon: "activity", color: "#6366F1" },
      { name: "Хранене и гени", desc: "Взаимодействия между храната и наследствеността", icon: "leaf", color: "#10B981" },
      { name: "Бъбреци", desc: "Бъбречен товар и подходящо хранене", icon: "heart-pulse", color: "#0EA5E9" },
      { name: "Кръвна картина", desc: "Желязо, витамин B12, фолиева киселина", icon: "droplets", color: "#EF4444" },
    ],
    ai_p2: "Работят като клиничен консилиум — превеждат данните в конкретни действия, а не в общи съвети.",
    science_title: "Науката зад Nutramilo",
    science_p1: "Базираме се на съвременни концепции от метаболитната ендокринология, нутригеномиката и изследванията на инсулиновия отговор към храната.",
    science_p2: "Всяко твърдение минава през слой „Научна обосновка“, който го сверява с международните научни бази. Виждате колко единодушни са изследванията, какъв тип са те и получавате пряка препратка към самата публикация.",
    models_title: "Ключови модели",
    model_1_title: "Инсулинови индекси на храната",
    model_1_desc: "Оценяват инсулиновия отговор след хранене, включително приноса на белтъчините.",
    model_2_title: "Модели за дългосрочно инсулиново натоварване",
    model_2_desc: "Отразяват натоварването през месеци и години и се свързват с показатели като инсулинова нечувствителност и кръвни мазнини.",
    model_3_title: "Метаболитни маркери и калкулатори",
    model_3_desc: "Единадесет клинични калкулатора: инсулинова нечувствителност, бъбречна функция, сърдечно-съдов риск, риск от диабет, телесна маса, витамин D, прием на вода, менструален цикъл, фибри, сън и риск от рак на гърдата. Плюс биологична възраст и оценка на плътността на гърдата. За всяка храна изчисляваме и ситост, хранителна плътност и поносимост при чувствителен стомах.",
    principle_title: "Научен принцип",
    principle_p1: "Разглеждаме инсулиновото натоварване като контекстно-зависим метаболитен модулатор — ефектът му зависи от композицията на тялото и индивидуалния хормонален фон.",
    how_title: "Как работи Nutramilo",
    how_steps: [
      { num: "1", title: "Анализ на храненето", desc: "Изчисляване на инсулиновия товар и контекста на храната" },
      { num: "2", title: "Метаболитна оценка", desc: "Обобщен резултат от 0 до 100" },
      { num: "3", title: "Контекстуална интерпретация", desc: "Съобразена с индивидуалния профил" },
      { num: "4", title: "Персонализирани насоки", desc: "Ненатрапчиви и адаптивни" },
    ],
    value_title: "Практическа стойност",
    value_items: [
      "Управление на метаболитното натоварване — без сляпо ограничаване на калориите",
      "Информиран хранителен избор — с обяснение защо, не само какво",
      "Дългосрочно подобрение на метаболитния баланс и енергията",
    ],
    disclaimer_title: "Важно",
    disclaimer_not: "Nutramilo не е медицински диагностичен инструмент, нито терапевтичен план или универсално решение.",
    disclaimer_is: "Nutramilo е система за метаболитна интелигентност и информирано хранене.",
    founder_title: "Създателят на платформата",
    founder_name: "Д-р Иван Инков",
    founder_role: "Гръден хирург и онкомамолог · Основател и научен директор на Nutramilo",
    founder_orcid: "ORCID: 0000-0001-6084-1707",
    founder_orcid_url: "https://orcid.org/0000-0001-6084-1707",
    founder_p1: "Д-р Иван Инков е специалист по гръдна хирургия и заболявания на млечната жлеза с фокус върху ранната диагностика, превенцията и персонализирания подход при рак на гърдата. С активен академичен профил (ORCID: 0000-0001-6084-1707), той интегрира клиничния си опит с научните постижения в областта на метаболитната ендокринология и създава мост между онкологията и персонализираното хранене.",
    founder_p2: "Неговите международни специализации формират основата на концепцията зад Nutramilo — платформа, която превежда сложните механизми на метаболизма, хормоналния отговор и канцерогенезата в практически приложими стратегии за превенция и лечение.",
    founder_credentials_title: "Д-р Инков притежава:",
    founder_credentials: [
      "Certificate of Competence in Breast Cancer (CCB) от University of Ulm — със задълбочена подготовка по биология, генетика, диагностика и лечение на рак на гърдата.",
      "Certificate of Advanced Studies (CAS) in Lung Cancer от University of Zurich — с фокус върху торакалната онкология и системния подход към онкологичните заболявания.",
      "Обучения в European School of Oncology, където е бил и официален посланик за България.",
    ],
    company_title: "Компания",
    company_desc: "International Sci Ink Press Ltd. е компанията зад Nutramilo. Специализирана в научни публикации и иновативни технологични решения в областта на здравето и храненето.",
    cta: "Присъединете се към Nutramilo",
    seo_desc: "Мисията, визията и научната основа на Nutramilo — инсулинов товар, специализирани модели и метаболитни профили.",
  },
  en: {
    title: "About Nutramilo",
    desc: "A network of 20 physiological twins, linked by more than 110 dependencies, that reads your body as one system rather than a pile of separate results. Metabolism, labs, sleep, heart, stress, hormones, oral and breast health — in one place, with a stated source behind every score.",
    back: "Home",
    mission_title: "Our Mission",
    mission_p1: "Nutramilo was created with a clear goal — to make metabolically oriented nutrition accessible, measurable, and personalized.",
    mission_p2: "Modern endocrinology shows that the response to food is individual and depends on: insulin sensitivity, β-cell function, body composition, hormonal status, and genetic factors.",
    mission_p3: "Nutramilo uses these principles to go beyond universal diets and offer a personalized metabolic model based on real data.",
    twin_title: "The Physiological Twin Network",
    twin_subtitle: "20 twins · one living system",
    twin_p1: "Nutramilo is not another step counter. It is a network of 20 twins for the separate systems of the body — nutrition, labs, heredity, microbiome, body composition, sleep, stress, heart, hormones, metabolism and insulin, inflammation, movement, liver, kidneys, teeth, breast, blood, nervous system, male health and breathing — combined into one shared score.",
    twin_p2: "Record a meal, measure your blood pressure or upload a lab result, and every affected twin updates at once. More than 110 physiological dependencies show what drags what: how this week's stress will meet your insulin curve, your heart and your gums next week. Every score opens up and states what it is built from.",
    twin_features: [
      "Conversation with the twins, including a spoken question — the answer is built only from your own data",
      "Biomarker map — 31 markers in 10 groups, each scored by exactly one owner twin",
      "Plate simulator — insulin load, cortisol response, blood lipids and renal mineral load",
      "The \"Today\" tile — one short entry drives several twins at once",
      "More than 110 physiological connections and vicious-circle detection",
      "KEGG biological pathways, each with an owner twin",
    ],
    consultants_cta_title: "Behind every twin — a physician",
    consultants_cta_sub: "Our medical consultants validate the algorithms and protocols. Meet the team.",
    consultants_cta_btn: "Meet the consultants",
    dil_title: "Dietary Insulin Load (DIL)",
    dil_p1: "At the core of the platform is the concept of Dietary Insulin Load (DIL) — a quantitative model that evaluates the metabolic insulin response to food, not just caloric intake.",
    dil_p2: "Unlike traditional approaches (calorie counting, glycemic index), DIL includes the effect of carbohydrates and proteins, reflects the real hormonal response, and serves as an indicator of metabolic load.",
    dil_p3: "Nutramilo uses DIL as a metabolic marker, not as a standalone disease predictor.",
    vision_title: "Vision & Philosophy",
    vision_p1: "\"Not all calories are metabolically equal.\" The insulin response to food depends on: macronutrient composition, fiber content, fat profile, degree of processing, and individual metabolic context.",
    vision_p2: "Nutramilo doesn't just count calories — the platform analyzes the metabolic load of every meal and interprets it in the context of the specific person.",
    personalization_title: "Personalization",
    personalization_p1: "Nutramilo defines metabolic phenotypes (P1–P5), reflecting: glucose control, insulin dynamics, lipid profile, and metabolic risk.",
    personalization_p2: "Recommendations adapt dynamically based on: current metabolic status, nutritional context, and physiological factors.",
    ai_title: "Intelligent System",
    ai_p1: "The platform integrates four AI specialists, each with access to a specialized scientific knowledge base:",
    ai_agents: [
      { name: "EndoBot", desc: "Metabolic and hormonal context analysis", icon: "activity", color: "#6366F1" },
      { name: "NutriBot", desc: "Nutrigenomics and nutritional interactions", icon: "leaf", color: "#10B981" },
      { name: "NephroBot", desc: "Nephrology — kidney diseases and diet", icon: "heart-pulse", color: "#0EA5E9" },
      { name: "HemoBot", desc: "Hematology — blood diseases and nutrition", icon: "droplets", color: "#EF4444" },
    ],
    ai_p2: "They work together to provide: personalized interpretation, contextual recommendations, and an adaptive nutritional model.",
    science_title: "The Science Behind Nutramilo",
    science_p1: "Nutramilo is based on modern scientific concepts from: metabolic endocrinology, nutrigenomics, and research on the insulin response to food.",
    science_p2: "A new Scientific Evidence layer automatically validates every AI clinical claim against PubMed (30M+ publications) and Semantic Scholar (200M+ papers). We show consensus %, weighted by study type (meta-analysis / RCT / cohort), real PMID links and Evidence Score dots next to every statement — an anti-hallucination layer that ensures we do not fabricate anything.",
    models_title: "Key Models",
    model_1_title: "Dietary Insulin Indices (DII, DIL)",
    model_1_desc: "Evaluate the postprandial insulin response to food, including the effect of proteins.",
    model_2_title: "Empirical Insulin Models (EDIH, EDIR)",
    model_2_desc: "Reflect long-term insulin load and are linked to metabolic markers such as insulin resistance and lipid profile.",
    model_3_title: "Metabolic Markers & Calculators",
    model_3_desc: "Nutramilo integrates 11 clinical calculators: HOMA-IR, eGFR (CKD-EPI 2021), CVD (Framingham), Diabetes (FINDRISC), BMI, Vitamin D, Water Intake, Menstrual Cycle, Fiber, Sleep and Breast Cancer Risk (Bulgaria GLOBOCAN 2024). Plus Biological Age (Levine PhenoAge) and Mammographic Density (BI-RADS) screening. Additionally — Endo Score, Satiety Score, Nutrient Density and FODMAP rating for every food.",
    principle_title: "Scientific Principle",
    principle_p1: "Nutramilo views insulin load as a context-dependent metabolic modulator. Its effect depends on: body composition, insulin sensitivity, β-cell function, and genetic profile.",
    how_title: "How Nutramilo Works",
    how_steps: [
      { num: "1", title: "Nutrition Analysis", desc: "DIL calculation and nutritional context" },
      { num: "2", title: "Metabolic Assessment", desc: "EndoScore (0–100)" },
      { num: "3", title: "Contextual Interpretation", desc: "Aligned with individual profile" },
      { num: "4", title: "Personalized Guidance", desc: "Non-intrusive and adaptive" },
    ],
    value_title: "Practical Value",
    value_items: [
      "Managing metabolic load",
      "Optimizing nutritional choices",
      "Long-term improvement of metabolic balance",
    ],
    disclaimer_title: "Important",
    disclaimer_not: "Nutramilo is not: a medical diagnostic tool, a diet plan or therapy, a universal solution.",
    disclaimer_is: "Nutramilo is: a system for metabolic intelligence and informed nutrition.",
    founder_title: "Founder",
    founder_name: "Dr. Ivan Inkov",
    founder_role: "Breast Surgeon & Oncomammologist · Founder & Scientific Director of Nutramilo",
    founder_orcid: "ORCID: 0000-0001-6084-1707",
    founder_orcid_url: "https://orcid.org/0000-0001-6084-1707",
    founder_p1: "Dr. Ivan Inkov is a breast surgeon and oncomammologist focused on early detection, prevention and a personalized approach to breast cancer. With an active academic profile (ORCID: 0000-0001-6084-1707), he combines clinical practice with research in metabolic endocrinology, building a bridge between oncology and personalized nutrition.",
    founder_p2: "His international specializations form the foundation of the Nutramilo concept — a platform that translates the complex mechanisms of metabolism, hormonal response and carcinogenesis into practical strategies for prevention and treatment.",
    founder_credentials_title: "Dr. Inkov holds:",
    founder_credentials: [
      "Certificate of Competence in Breast Cancer (CCB) from the University of Ulm — in-depth training in the biology, genetics, diagnostics and treatment of breast cancer.",
      "Certificate of Advanced Studies (CAS) in Lung Cancer from the University of Zurich — focused on thoracic oncology and the systemic approach to oncologic disease.",
      "Training with the European School of Oncology, where he also served as the official ambassador for Bulgaria.",
    ],
    company_title: "Company",
    company_desc: "International Sci Ink Press Ltd. is the company behind Nutramilo. Specializing in scientific publications and innovative technology solutions in health and nutrition.",
    cta: "Join Nutramilo",
    seo_desc: "The mission, vision and science behind Nutramilo. DIL model, 4 AI specialists, metabolic phenotypes.",
  },
  es: {
    title: "Sobre Nutramilo",
    desc: "Una red de 20 gemelos fisiológicos, unidos por más de 110 dependencias, que lee su cuerpo como un solo sistema y no como análisis sueltos. Metabolismo, laboratorio, sueño, corazón, tensión nerviosa, hormonas, salud oral y mamaria — en un solo lugar, con la fuente declarada detrás de cada puntuación.",
    twin_title: "La red de gemelos fisiológicos",
    twin_subtitle: "20 gemelos · un sistema vivo",
    twin_p1: "Nutramilo no es otro contador de pasos. Es una red de 20 gemelos para los distintos sistemas del organismo — nutrición, laboratorio, herencia, microbioma, composición corporal, sueño, tensión nerviosa, corazón, hormonas, metabolismo e insulina, inflamación, movimiento, hígado, riñones, dientes, mama, sangre, sistema nervioso, salud masculina y respiración — reunidos en una puntuación común.",
    twin_p2: "Cuando anota una comida, mide la tensión arterial o sube un análisis, todos los gemelos afectados se actualizan a la vez. Más de 110 dependencias fisiológicas muestran qué arrastra a qué: cómo la tensión de esta semana se cruzará con su curva de insulina, con el corazón y con las encías la semana siguiente. Cada puntuación se abre y declara de qué está compuesta.",
    twin_features: [
      "Conversación con los gemelos, también con pregunta por voz — la respuesta se compone solo con sus datos",
      "Mapa de marcadores — 31 marcadores en 10 grupos, cada uno con un solo gemelo responsable",
      "Simulador del plato — carga de insulina, respuesta del cortisol, lípidos y carga renal de minerales",
      "La casilla «Hoy» — una anotación breve mueve varios gemelos a la vez",
      "Más de 110 conexiones fisiológicas y detección de círculos viciosos",
      "Rutas biológicas KEGG, cada una con un gemelo responsable",
    ],
    consultants_cta_title: "Detrás de cada gemelo hay un médico",
    consultants_cta_sub: "Nuestros consultores médicos revisan los algoritmos y los protocolos. Conozca al equipo.",
    consultants_cta_btn: "Ver los consultores",
    back: "Inicio",
    mission_title: "Nuestra mision",
    mission_p1: "Nutramilo fue creado con un objetivo claro — hacer la nutricion orientada al metabolismo accesible, medible y personalizada.",
    mission_p2: "La endocrinologia moderna muestra que la respuesta a los alimentos es individual y depende de: sensibilidad a la insulina, funcion de las celulas beta, composicion corporal, estado hormonal y factores geneticos.",
    mission_p3: "Nutramilo utiliza estos principios para ir mas alla de las dietas universales y ofrecer un modelo metabolico personalizado basado en datos reales.",
    dil_title: "Carga Insulinica Dietetica (DIL)", dil_p1: "En el nucleo de la plataforma esta el concepto de DIL — un modelo cuantitativo que evalua la respuesta metabolica de insulina a los alimentos.", dil_p2: "A diferencia de los enfoques tradicionales, DIL incluye el efecto de carbohidratos y proteinas y refleja la respuesta hormonal real.", dil_p3: "Nutramilo usa DIL como marcador metabolico, no como predictor independiente de enfermedad.",
    vision_title: "Vision y filosofia", vision_p1: "\"No todas las calorias son metabolicamente iguales.\" La respuesta insulinica a la comida depende del perfil de macronutrientes, fibra, grasas y el contexto metabolico individual.", vision_p2: "Nutramilo no solo cuenta calorias — analiza la carga metabolica de cada comida e la interpreta en el contexto de la persona.",
    personalization_title: "Personalizacion", personalization_p1: "Nutramilo define fenotipos metabolicos (P1-P5) que reflejan control de glucosa, dinamica de insulina, perfil lipidico y riesgo metabolico.", personalization_p2: "Las recomendaciones se adaptan dinamicamente segun el estado metabolico actual, el contexto nutricional y factores fisiologicos.",
    ai_title: "Sistema inteligente", ai_p1: "La plataforma integra cuatro especialistas de IA, cada uno con acceso a una base de conocimiento cientifico especializada:",
    ai_agents: [
      { name: "EndoBot", desc: "Analisis del contexto metabolico y hormonal", icon: "activity", color: "#6366F1" },
      { name: "NutriBot", desc: "Nutrigenomica e interacciones nutricionales", icon: "leaf", color: "#10B981" },
      { name: "NephroBot", desc: "Nefrologia — enfermedades renales y dieta", icon: "heart-pulse", color: "#0EA5E9" },
      { name: "HemoBot", desc: "Hematologia — enfermedades de la sangre y nutricion", icon: "droplets", color: "#EF4444" },
    ],
    ai_p2: "Trabajan juntos para proporcionar: interpretacion personalizada, recomendaciones contextuales y un modelo nutricional adaptativo.",
    science_title: "La ciencia detras de Nutramilo", science_p1: "Nutramilo se basa en conceptos cientificos modernos de endocrinologia metabolica, nutrigenomica e investigacion sobre la respuesta insulinica.",
    models_title: "Modelos clave", model_1_title: "Indices insulinicos dieteticos (DII, DIL)", model_1_desc: "Evaluan la respuesta insulinica posprandial incluyendo el efecto de las proteinas.", model_2_title: "Modelos insulinicos empiricos (EDIH, EDIR)", model_2_desc: "Reflejan la carga insulinica a largo plazo y estan vinculados a marcadores metabolicos.", model_3_title: "Marcadores metabolicos y calculadoras", model_3_desc: "Nutramilo integra 11 calculadoras clinicas: HOMA-IR, eGFR (CKD-EPI 2021), CVD (Framingham), Diabetes (FINDRISC), BMI, Vitamina D, Ingesta de agua, Ciclo menstrual, Fibra, Sueno y Riesgo de cancer de mama (Bulgaria GLOBOCAN 2024). Mas edad biologica (Levine PhenoAge) y densidad mamografica (BI-RADS). Ademas — Endo Score, Satiety Score, Nutrient Density y calificacion FODMAP para cada alimento.",
    principle_title: "Principio cientifico", principle_p1: "Nutramilo ve la carga insulinica como un modulador metabolico dependiente del contexto.",
    how_title: "Como funciona Nutramilo",
    how_steps: [
      { num: "1", title: "Analisis nutricional", desc: "Calculo de DIL y contexto nutricional" },
      { num: "2", title: "Evaluacion metabolica", desc: "EndoScore (0-100)" },
      { num: "3", title: "Interpretacion contextual", desc: "Alineada con el perfil individual" },
      { num: "4", title: "Orientacion personalizada", desc: "No intrusiva y adaptativa" },
    ],
    value_title: "Valor practico", value_items: ["Gestion de la carga metabolica", "Optimizacion de la eleccion nutricional", "Mejora a largo plazo del equilibrio metabolico"],
    disclaimer_title: "Importante", disclaimer_not: "Nutramilo no es: una herramienta de diagnostico medico, un plan de dieta o terapia, una solucion universal.", disclaimer_is: "Nutramilo es: un sistema de inteligencia metabolica y nutricion informada.",
    founder_title: "Fundador", founder_name: "Dr. Ivan Inkov",
    founder_role: "Cirujano de mama y oncomamologo · Fundador y Director Cientifico de Nutramilo",
    founder_orcid: "ORCID: 0000-0001-6084-1707",
    founder_orcid_url: "https://orcid.org/0000-0001-6084-1707",
    founder_p1: "El Dr. Ivan Inkov es cirujano de mama y oncomamologo, especializado en deteccion temprana, prevencion y enfoque personalizado del cancer de mama. Con un perfil academico activo (ORCID: 0000-0001-6084-1707), integra la practica clinica con la investigacion en endocrinologia metabolica.",
    founder_p2: "Sus especializaciones internacionales son la base del concepto Nutramilo — una plataforma que traduce los mecanismos complejos del metabolismo, la respuesta hormonal y la carcinogenesis en estrategias practicas de prevencion y tratamiento.",
    founder_credentials_title: "El Dr. Inkov posee:",
    founder_credentials: [
      "Certificate of Competence in Breast Cancer (CCB) — University of Ulm.",
      "Certificate of Advanced Studies (CAS) in Lung Cancer — University of Zurich.",
      "Formacion con la European School of Oncology, donde fue embajador oficial para Bulgaria.",
    ],
    company_title: "Empresa", company_desc: "International Sci Ink Press Ltd. es la empresa detras de Nutramilo.",
    cta: "Unete a Nutramilo", seo_desc: "La mision, vision y ciencia detras de Nutramilo.",
  },
};

const ICON_MAP = { activity: Activity, leaf: Heart, "heart-pulse": HeartPulse, droplets: Droplets };

export default function AboutPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX.en;
  const card = `p-5 rounded-2xl border ${isDark ? 'bg-[hsl(223,47%,8%)] border-[hsl(223,47%,14%)]' : 'bg-white border-slate-100 shadow-sm'}`;
  const heading = `text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`;
  const body = `text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-background text-foreground' : 'bg-[#F9F9F7]'}`} data-testid="about-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={t.title} description={t.seo_desc} path="/about" />
      <SubpageNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="pt-8">
          <SubpageBackLink label={t.back} />
        </div>

        {/* Hero */}
        <SubpageHero
          eyebrow={language === "bg" ? "За Nutramilo" : language === "es" ? "Sobre Nutramilo" : "About Nutramilo"}
          title={t.title}
          subtitle={t.desc}
          meta={language === "bg"
            ? ["Създадена от практикуващ лекар", "Образователна платформа", "Разработена в България"]
            : language === "es"
            ? ["Creada por un médico en ejercicio", "Plataforma educativa", "Desarrollada en Bulgaria"]
            : ["Built by a practising clinician", "Educational platform", "Developed in Bulgaria"]}
          icon={Target}
          accent="#6366F1"
          testId="about-hero"
        />
        <div className="mb-10" />

        {/* Mission */}
        <ScienceUxSection isDark={isDark} language={language} />
        <Section icon={Target} gradient="from-[#6366F1] to-[#8B5CF6]" title={t.mission_title} isDark={isDark}>
          <p className={body}>{t.mission_p1}</p>
          <p className={body}>{t.mission_p2}</p>
          <p className={body}>{t.mission_p3}</p>
        </Section>

        {/* Digital Health Twin — iter636 */}
        {t.twin_title && (
          <Section icon={Layers} gradient="from-[#EC4899] to-[#8B5CF6]" title={t.twin_title} isDark={isDark}>
            <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-fuchsia-300" : "text-fuchsia-700"}`}>
              {t.twin_subtitle}
            </p>
            <p className={body}>{t.twin_p1}</p>
            <p className={body}>{t.twin_p2}</p>
            {Array.isArray(t.twin_features) && (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="twin-features-list">
                {t.twin_features.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-xs leading-relaxed px-3 py-2 rounded-lg border ${isDark ? "border-fuchsia-500/30 bg-fuchsia-500/5 text-slate-300" : "border-fuchsia-100 bg-fuchsia-50/50 text-slate-700"}`}>
                    <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: "var(--brand-pink)" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {/* DIL */}
        <Section icon={Beaker} gradient="from-[#0EA5E9] to-[#06B6D4]" title={t.dil_title} isDark={isDark}>
          <p className={body}>{t.dil_p1}</p>
          <p className={body}>{t.dil_p2}</p>
          <p className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{t.dil_p3}</p>
        </Section>

        {/* Vision */}
        <Section icon={Lightbulb} gradient="from-[#F59E0B] to-[#EF4444]" title={t.vision_title} isDark={isDark}>
          <p className={`text-sm font-semibold italic ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{t.vision_p1}</p>
          <p className={body}>{t.vision_p2}</p>
        </Section>

        {/* Personalization */}
        <Section icon={Dna} gradient="from-[#8B5CF6] to-[#EC4899]" title={t.personalization_title} isDark={isDark}>
          <p className={body}>{t.personalization_p1}</p>
          <p className={body}>{t.personalization_p2}</p>
        </Section>

        {/* AI System */}
        <Section icon={Brain} gradient="from-[#10B981] to-[#0EA5E9]" title={t.ai_title} isDark={isDark}>
          <p className={body}>{t.ai_p1}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
            {t.ai_agents.map(agent => {
              const Icon = ICON_MAP[agent.icon] || Activity;
              return (
                <div key={agent.name} className={card} data-testid={`ai-agent-${agent.name.toLowerCase()}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${agent.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: agent.color }} />
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{agent.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{agent.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className={body}>{t.ai_p2}</p>
        </Section>

        {/* Science */}
        <Section icon={Microscope} gradient="from-[#EF4444] to-[#F59E0B]" title={t.science_title} isDark={isDark}>
          <p className={body}>{t.science_p1}</p>
          {t.science_p2 && (
            <div className={`${card} mt-4 mb-2 border-l-4 border-[#10B981]`}>
              <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{t.science_p2}</p>
            </div>
          )}

          <h3 className={`text-base font-bold mt-5 mb-3 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{t.models_title}</h3>
          {[
            { title: t.model_1_title, desc: t.model_1_desc },
            { title: t.model_2_title, desc: t.model_2_desc },
            { title: t.model_3_title, desc: t.model_3_desc },
          ].map(m => (
            <div key={m.title} className={`${card} mb-3`}>
              <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{m.title}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{m.desc}</p>
            </div>
          ))}

          <h3 className={`text-base font-bold mt-5 mb-2 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{t.principle_title}</h3>
          <p className={body}>{t.principle_p1}</p>
        </Section>

        {/* How it Works */}
        <Section icon={Activity} gradient="from-[#06B6D4] to-[#6366F1]" title={t.how_title} isDark={isDark}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {t.how_steps.map(s => (
              <div key={s.num} className={card + " text-center"}>
                <div className="text-2xl font-bold text-[#0EA5E9] mb-1">{s.num}</div>
                <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{s.title}</p>
                <p className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{s.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Value */}
        <Section icon={Target} gradient="from-[#10B981] to-[#059669]" title={t.value_title} isDark={isDark}>
          <div className="space-y-2">
            {t.value_items.map(item => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] flex-shrink-0" />
                <p className={body}>{item}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Disclaimer */}
        <section className="mb-14" data-testid="disclaimer-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EF4444] to-[#F97316] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h2 className={heading} style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.disclaimer_title}</h2>
          </div>
          <div className={`${card} space-y-3`}>
            <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{t.disclaimer_not}</p>
            <p className={`text-sm font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{t.disclaimer_is}</p>
          </div>
        </section>

        {/* Founder */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <h2 className={heading} style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.founder_title}</h2>
          </div>
          <div className={card} data-testid="founder-card">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#6366F1] flex items-center justify-center shrink-0">
                <span className="text-white text-xl sm:text-2xl font-bold">ИИ</span>
              </div>
              <div>
                <h3 className={`text-lg font-bold mb-0.5 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{t.founder_name}</h3>
                <p className="text-sm text-[#00796B] mb-1 font-medium">{t.founder_role}</p>
                {t.founder_orcid && (
                  <a href={t.founder_orcid_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs font-mono mb-3 text-[#A6CE39] hover:underline"
                     data-testid="founder-orcid-link">
                    <svg viewBox="0 0 256 256" className="w-3.5 h-3.5" fill="currentColor"><path d="M128 0C57.3 0 0 57.3 0 128s57.3 128 128 128 128-57.3 128-128S198.7 0 128 0zM86.3 186.2H70.9V79.1h15.4v107.1zm-7.7-118.6c-5.3 0-9.6-4.3-9.6-9.6s4.3-9.6 9.6-9.6 9.6 4.3 9.6 9.6-4.3 9.6-9.6 9.6zm107.4 118.6h-15.3v-52c0-12.4-.2-28.4-17.3-28.4-17.3 0-20 13.5-20 27.5v52.9h-15.3V79.1h14.7v14.7h.2c2.1-3.9 7-8 14.5-8 15.5 0 18.4 10.2 18.4 23.5v76.9z"/></svg>
                    {t.founder_orcid}
                  </a>
                )}
                <div className={`text-sm leading-relaxed space-y-3 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  <p>{t.founder_p1}</p>
                  <p>{t.founder_p2}</p>
                  {t.founder_credentials && t.founder_credentials.length > 0 && (
                    <div className="pt-1" data-testid="founder-credentials">
                      <p className={`font-semibold mb-1.5 ${isDark ? 'text-slate-200' : 'text-[#1A1A2E]'}`}>{t.founder_credentials_title}</p>
                      <ul className="space-y-1.5 list-disc pl-5 marker:text-[#10B981]">
                        {t.founder_credentials.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Consultants CTA — iter636 */}
        {t.consultants_cta_title && (
          <section className="mb-14" data-testid="consultants-cta-section">
            <div className={`p-6 sm:p-7 rounded-2xl border ${isDark ? "bg-gradient-to-br from-fuchsia-500/10 to-cyan-500/10 border-fuchsia-500/30" : "bg-gradient-to-br from-fuchsia-50 to-cyan-50 border-fuchsia-200"}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#EC4899] to-[#06B6D4] flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.consultants_cta_title}</h3>
                  <p className={body}>{t.consultants_cta_sub}</p>
                </div>
                <Link
                  to="/consultants"
                  data-testid="about-consultants-link"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold text-white shadow-md hover:shadow-lg transition-shadow flex-shrink-0"
                  style={{ background: "var(--brand-gradient-primary)" }}
                >
                  {t.consultants_cta_btn}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Company */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EC4899] to-[#F43F5E] flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <h2 className={heading} style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.company_title}</h2>
          </div>
          <div className={card} data-testid="company-card">
            <div className="flex items-center gap-3 mb-2">
              <img src="/sci-ink-press-logo.png" alt="Sci Ink Press" className="h-10 w-auto" onError={e => e.target.style.display = "none"} />
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>International Sci Ink Press Ltd.</h3>
            </div>
            <p className={body}>{t.company_desc}</p>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <Link to="/register">
            <button className="bg-gradient-to-r from-[#7B1FA2] to-[#F06292] hover:from-[#6A1B9A] hover:to-[#E91E63] text-white px-8 py-3 text-sm font-semibold rounded-full shadow-lg shadow-purple-500/20 transition-all" data-testid="about-cta-btn">
              {t.cta}
            </button>
          </Link>
        </div>
      </div>

      <SubpageFooter />
    </div>
  );
}

function Section({ icon: Icon, gradient, title, isDark, children }) {
  return (
    <section className="mb-14">
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
          {title}
        </h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ScienceUxSection — high-impact visual hero placed above Mission.
// Shows the diary-components-in-synergy + the two proprietary scores
// (NIS + Personalised DIL) as a *narrative*, not as formulas.
// All copy in clean Bulgarian (no transliterations), patient-friendly UX.
// ─────────────────────────────────────────────────────────────────────
function ScienceUxSection({ isDark, language }) {
  const bg = language === "bg";
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  const pillars = [
    {
      icon: Utensils,
      grad: "from-[#F59E0B] to-[#EF4444]",
      title: L("Хранителен дневник", "Food diary", "Diario de alimentación"),
      body: L(
        "Всяка хапка става точен сигнал — макроси, фибри, тип мазнини, степен на преработка, скорост на усвояване.",
        "Every bite becomes a precise signal — macros, fibre, fat type, degree of processing, absorption speed.",
        "Cada bocado se convierte en una señal precisa — macros, fibra, tipo de grasa, grado de procesado, velocidad de absorción."
      ),
    },
    {
      icon: LineChart,
      grad: "from-[#7C3AED] to-[#EC4899]",
      title: L("Инсулинов отговор на хранене", "Insulin response per meal", "Respuesta insulínica por comida"),
      body: L(
        "Очакваният двучасов инсулинов отговор на конкретното хранене, изразен като дял спрямо чистата глюкоза.",
        "The expected two-hour insulin response of a specific meal, as a share of pure glucose.",
        "La respuesta insulínica esperada a dos horas de una comida concreta, como proporción respecto a la glucosa pura."
      ),
    },
    {
      icon: Layers,
      grad: "from-[#0EA5E9] to-[#06B6D4]",
      title: L("Личен дневен инсулинов товар", "Personal daily insulin load", "Carga insulínica diaria personal"),
      body: L(
        "Сборният инсулинов товар за деня, претеглен спрямо Вашето тегло, активност и обмяна.",
        "The total insulin load for the day, weighted by your weight, activity and metabolism.",
        "La carga insulínica total del día, ponderada según su peso, actividad y metabolismo."
      ),
    },
    {
      icon: Dna,
      grad: "from-[#8B5CF6] to-[#EC4899]",
      title: L("Метаболитен фенотип", "Metabolic phenotype", "Fenotipo metabólico"),
      body: L(
        "Кой механизъм води при Вас — инсулин, лептин, щитовидна жлеза, кортизол или половите хормони.",
        "Which mechanism leads for you — insulin, leptin, thyroid, cortisol or sex hormones.",
        "Qué mecanismo predomina en usted — insulina, leptina, tiroides, cortisol u hormonas sexuales."
      ),
    },
    {
      icon: Brain,
      grad: "from-[#10B981] to-[#0EA5E9]",
      title: L("Консилиум от специализирани модели", "A panel of specialist models", "Un consejo de modelos especialistas"),
      body: L(
        "Четири специализирани модела — по хормони, хранене, бъбреци и кръвна картина — превеждат данните в конкретни действия.",
        "Four specialist models — for hormones, nutrition, kidneys and blood count — translate the data into concrete actions.",
        "Cuatro modelos especialistas — hormonas, nutrición, riñón y hemograma — traducen los datos en acciones concretas."
      ),
    },
    {
      icon: Zap,
      grad: "from-[#EF4444] to-[#F59E0B]",
      title: L("Синергия между модулите", "Module synergy", "Sinergia entre módulos"),
      body: L(
        "Дневникът дава сигнала, сигналът оформя дневния товар, товарът очертава профила Ви, а моделите обясняват причините и предлагат решения.",
        "The diary gives the signal, the signal shapes the daily load, the load outlines your profile, and the models explain the causes and offer solutions.",
        "El diario aporta la señal, la señal moldea la carga diaria, la carga perfila su patrón, y los modelos explican las causas y proponen soluciones."
      ),
    },
  ];

  const benefits = [
    {
      label: L("Отговор на хранене", "Meal response", "Respuesta a la comida"),
      title: L("Какво показва", "What it shows", "Qué muestra"),
      body: L(
        "Очакваният инсулинов отговор на конкретно хранене. Изчислява се по рецензирана научна формула, проверена върху 147 храни.",
        "The expected insulin response to a specific meal. Calculated with a peer-reviewed formula, validated on 147 foods.",
        "La respuesta insulínica esperada a una comida concreta. Calculada con una fórmula revisada por pares, validada en 147 alimentos."
      ),
      benefit: L(
        "Ползата: разбирате защо тялото Ви реагира така — преди скокът да настъпи.",
        "The benefit: you understand why your body reacts the way it does — before the spike happens.",
        "El beneficio: entiende por qué su cuerpo reacciona así — antes de que llegue el pico."
      ),
    },
    {
      label: L("Дневен товар", "Daily load", "Carga diaria"),
      title: L("Какво показва", "What it shows", "Qué muestra"),
      body: L(
        "Дневният Ви инсулинов товар, претеглен спрямо тегло, активност и това, което изследванията показват за обмяната Ви.",
        "Your daily insulin load, weighted by weight, activity and what your lab results show about your metabolism.",
        "Su carga insulínica diaria, ponderada por peso, actividad y lo que sus análisis dicen sobre su metabolismo."
      ),
      benefit: L(
        "Ползата: знаете дневния си таван и виждате кога го надвишавате, без да чакате нова кръвна картина.",
        "The benefit: you know your daily ceiling and see when you exceed it, without waiting for new bloodwork.",
        "El beneficio: conoce su límite diario y ve cuándo lo supera, sin esperar un nuevo análisis."
      ),
    },
  ];

  return (
    <section data-testid="about-science-ux" className="mb-16">
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5 text-xs font-semibold tracking-wide uppercase ${isDark ? "border-[hsl(223,47%,22%)] text-slate-300 bg-[hsl(223,47%,12%)]" : "border-slate-200 text-slate-600 bg-white"}`}>
        <Sparkles className="w-3.5 h-3.5 text-[#e44288]" />
        {L("Базирано на доказателства", "Evidence-based", "Basado en evidencia")}
      </div>
      <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
        {L("Науката — преведена в дневен ритуал", "Science — translated into a daily ritual", "La ciencia — convertida en un ritual diario")}
      </h2>
      <p className={`text-sm leading-relaxed mb-8 max-w-3xl ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        {L(
          "Не Ви засипваме с формули. Показваме как се свързват отделните части на дневника Ви и какво научавате за тялото си всеки ден.",
          "We don't flood you with formulas. We show how the components of your food diary interact — and what you learn about your body every day.",
          "No le abrumamos con fórmulas. Le mostramos cómo se conectan las partes de su diario y qué aprende cada día sobre su cuerpo."
        )}
      </p>

      {/* Pillars grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
        {pillars.map((p, idx) => {
          const Icon = p.icon;
          return (
            <div
              key={`pillar-${idx}-${p.title.slice(0, 12)}`}
              data-testid={`about-pillar-${idx}`}
              className={`relative p-5 rounded-2xl border overflow-hidden ${
                isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,8%)]" : "border-slate-200 bg-white"
              }`}
            >
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${p.grad} inline-flex items-center justify-center mb-3 shadow-sm`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <h3 className={`text-sm font-bold mb-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{p.title}</h3>
              <p className={`text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>{p.body}</p>
            </div>
          );
        })}
      </div>

      {/* Benefit tiles */}
      <div className="grid md:grid-cols-2 gap-4">
        {benefits.map((b, idx) => (
          <div
            key={`benefit-${b.label}`}
            data-testid={`about-formula-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`p-6 rounded-2xl border ${
              isDark ? "border-[hsl(223,47%,18%)] bg-gradient-to-br from-[hsl(223,47%,9%)] to-[hsl(282,47%,11%)]" : "border-slate-200 bg-gradient-to-br from-white to-purple-50/30"
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${idx === 0 ? "bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white" : "bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] text-white"}`}>
                {b.label}
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {b.title}
              </span>
            </div>
            <p className={`text-sm leading-relaxed mb-2 ${isDark ? "text-slate-200" : "text-slate-700"}`}>
              {b.body}
            </p>
            <p className={`text-xs font-semibold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              {b.benefit}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link to="/knowledge-library">
          <div data-testid="about-science-cta-papers" className="inline-flex items-center gap-2 text-sm font-semibold px-5 h-11 rounded-full text-white brand-cta-primary">
            {L("Виж публикациите", "See the papers", "Ver las publicaciones")}
            <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
        <Link
          to="/smart/plate-simulator"
          data-testid="about-science-cta-simulator"
          className={`text-sm font-semibold ${isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-[#287a2e]"}`}
        >
          {L("Опитайте Симулатора на чинията →", "Try the Plate Simulator →", "Pruebe el Simulador del plato →")}
        </Link>
      </div>
    </section>
  );
}
